import { get } from 'lodash';
import EventBus, { Event, Unsubscriber } from '@/core/event-bus';
import { SubtitleTrack, PlayerState } from '../player-state';

export function detectSubtitleTracks(trackList: TextTrackList | null, updateState: (partialState: Partial<PlayerState>) => void): void {
    const tracksFromVideo = extractVideoSubtitleTracks(trackList);

    const subtitleTracks = tracksFromVideo.map(toSubtitleTrack)
        .filter(t => !!t) as SubtitleTrack[];

    subtitleTracks.sort((a: SubtitleTrack, b: SubtitleTrack) => {
        if (!a.label || !b.label) {
            return 0;
        }
        return a.label < b.label ? -1 : 1;
    });

    updateState({ subtitleTracks: subtitleTracks });
}

export function disableSubtitles(trackList: TextTrackList | null, updateState: (partialState: Partial<PlayerState>) => void): void {
    disableSubtitleTracks(trackList);
    updateState({ subtitlesEnabled: false });
}

export function selectSubtitleTrackHandler(
    eventBus: EventBus,
    video: HTMLVideoElement,
    updateState: (partialState: Partial<PlayerState>) => void
): Unsubscriber {

    updateState({ subtitles: [] });

    return eventBus.on(
        Event.selectSubtitleTrack,
        payload => {

            disableSubtitleTracks(video.textTracks);

            const track = get(payload, 'track') as SubtitleTrack;

            if (!track) {
                return;
            }

            const targetTrack = extractVideoSubtitleTracks(video.textTracks)
                .find(t => generateTrackId(t) === track.id);

            if (!targetTrack) {
                return;
            }

            updateState({ subtitleTrackActive: toSubtitleTrack(targetTrack) });
            updateState({ subtitlesEnabled: true });

            targetTrack.mode = 'hidden';

            targetTrack.oncuechange = (event) => {
                const target = get(event, 'target') as TextTrack;
                if (!target || !target.activeCues || !target.activeCues.length) {
                    return;
                }
                const cues = target.activeCues || [];

                const subtitles = Array.from(cues)
                    .map(c => get(c, 'text', ''))
                    .reverse();
                // Here we handle captions arriving sequentially in the incorrect order for display (SS-25114) hence the reverse
                // lines breaks are handled in the subtitles widget

                updateState({ subtitles });
            };
        }
    );
}

export function disableSubtitlesHandler(
    eventBus: EventBus,
    video: HTMLVideoElement,
    updateState: (partialState: Partial<PlayerState>) => void
): Unsubscriber {

    updateState({ subtitles: [] });

    return eventBus.on(
        Event.disableSubtitles,
        () => disableSubtitles(video.textTracks, updateState)
    );
}

function disableSubtitleTracks(trackList: TextTrackList | null): void {
    const subtitleTracks = extractVideoSubtitleTracks(trackList);
    subtitleTracks.forEach(track => {
        track.mode = 'disabled';
        track.oncuechange = null;
    });
}

function extractVideoSubtitleTracks(trackList: TextTrackList | null): TextTrack[] {
    const subtitleTracks = [];

    if (trackList) {
        for (let i = 0; i < trackList.length; ++i) {
            const track = trackList[i];
            if (track.kind === 'subtitles' || track.kind === 'captions') {
                subtitleTracks.push(track);
            }
        }
    }

    return subtitleTracks;
}

function toSubtitleTrack(track: TextTrack | null): SubtitleTrack | null {
    if (!track) {
        return null;
    }

    return {
        id: generateTrackId(track),
        language: track.language,
        label: track.label
    };
}

function generateTrackId(track: TextTrack): string {
    return `${track.language}-${track.label}`;
}
